'use client'

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Camera, Fingerprint, Loader2, Trash2 } from 'lucide-react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { EntryDetailShell, type DetailBadge } from '../EntryDetail/EntryDetailShell'
import { RefreshControl } from '../BaseDashboard'
import { InvestigationSidebar, type InvestigationAction } from '../InvestigationSidebar'
import { browserLoadActions } from './browserLoadActions'
import { detailGridCols } from '../EntryDetail/EntryDetailPanel'
import type { SibsEntry } from '../EntryDetail/SibsWidgets'
import { DomainTakedownWidget } from '../EntryDetail/DomainTakedownWidget'
import { ActionsWidget } from '../EntryDetail/ActionsWidget'
import type { Communication } from '@shared/postui'
import { EntryTimelineWidget } from '../EntryDetail/EntryTimelineWidget'
import { ProcessedEntryTimeline } from '../ProcessedEntry/DomainEntry/ProcessedEntryTimeline'
import { LatestObtainedImagesPanel } from '../ProcessedEntry/DomainEntry/LatestObtainedImagesPanel'
import { DomainIntelligencePanel } from '../ProcessedEntry/DomainEntry/DomainIntelligencePanel'
import { BrowserDataPanel } from '../ProcessedEntry/DomainEntry/BrowserDataPanel'
import { ProbeRunsPanel } from '../ProcessedEntry/DomainEntry/ProbeRunsPanel'
import type { BrowserRow, EntryDetailData, ProcessedEntry, ProcessedTheme } from '../ProcessedEntry/DomainEntry/processedTypes'
import { watchClasses } from '../ProcessedEntry/DomainEntry/processedTypes'
import type {
  CampaignDomainIdentifier,
  CampaignEntityReferenceIdentifier,
  EntryDomainLink,
  EntryEntityReferenceLink,
} from './campaignTypes'
import { deriveActionableEvidence } from './AdminSubmitEntryView'
import { getEntryIsScam } from './CasosView'
import { RawProcessedEntryPanel } from './RawProcessedEntryPanel'
import { CollapsibleSection } from './CollapsibleSection'
import { SourcingProvenanceSection, readAutoDomainProvenance } from './SourcingProvenanceSection'
import { GenericEntryRow } from './GenericEntryRow'
import type { Catalog, EntryRow } from './entryTypes'
import { parseDbTimestamp } from '@/lib/dates'

// Same revision_status vocabulary CasesDashboardView's list column and
// EntryTimeline's stage labels use — duplicated locally (small enough, and
// each of those two already keeps its own copy) rather than imported, so
// this header badge doesn't take on a cross-component dependency for six
// string pairs.
const REVISION_BADGE: Record<string, { label: string; tone: DetailBadge['tone'] }> = {
  unseen: { label: 'Pendente', tone: 'neutral' },
  accept: { label: 'Aceite', tone: 'emerald' },
  'auto-accept': { label: 'Auto', tone: 'emerald' },
  'accept-hidden': { label: 'Aceite (oculto)', tone: 'emerald' },
  reject: { label: 'Rejeitado', tone: 'red' },
  defer1: { label: 'Adiado', tone: 'amber' },
}

function fmtDate(iso?: string) {
  const d = parseDbTimestamp(iso)
  if (!d) return '—'
  const time = new Intl.DateTimeFormat('pt-PT', { hour: '2-digit', minute: '2-digit' }).format(d)
  const date = new Intl.DateTimeFormat('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(d)
  return `${time} ${date}`
}

// sibsCommunication (SibsWidgets.tsx) always renders SMS regardless of
// channel — fine for SIBS's bank-fraud entries (almost always SMS/MBWay),
// but Cases spans every channel including Email. message_subject alone isn't
// reliable (plenty of real emails come through with no subject captured), so
// this leads with the resolved message-type catalog name (e.g. "Email") and
// only falls back to "a subject is present" when the type couldn't be
// resolved.
function caseEntryCommunication(raw: any, receivedAt: string, messageTypeText?: string): Communication | undefined {
  const body = typeof raw?.message_content === 'string' ? raw.message_content : undefined
  if (!body) return undefined
  const sender = typeof raw?.sender === 'string' && raw.sender ? raw.sender : 'Unknown'
  const subject = typeof raw?.message_subject === 'string' && raw.message_subject ? raw.message_subject : undefined
  const isEmail = messageTypeText?.toLowerCase() === 'email' || !!subject
  return isEmail
    ? { type: 'email', sender, subject: subject ?? '(no subject)', receivedAt, body }
    : { type: 'sms', sender, receivedAt, body }
}

function caseEntryTitle(item: EntryRow): string {
  const raw = item.raw_entries ?? item
  if (raw.message_content) return String(raw.message_content).slice(0, 90)
  if (raw.sender) return String(raw.sender)
  return `Entry · ${item.id}`
}

// Same technique as AdminSubmitEntryView's sibsEntryShape — the SIBS widget
// library (SibsPipelineWidget, PayloadWidgetAdapter, sibsCommunication, …)
// expects a SibsEntry, which this generic Cases row doesn't naturally have;
// entity_reference/domain/beneficiario are pulled from whichever identifier
// columns the incoming row shape actually has (see toSibsEntryAdapter below),
// everything else not derivable from a plain EntryRow is nulled out, and
// every widget already renders gracefully empty for missing fields rather
// than crashing.
function toSibsEntryAdapter(item: EntryRow, platforms: Catalog['platforms'] = []): SibsEntry {
  const raw = item.raw_entries ?? item
  // Two row shapes reach this page and they name their identifier columns
  // differently:
  //   Cases (raw_entries_flat / processed_entries_flat) — bare domain_id /
  //     entity_reference_id / entity_reference_beneficiary, still present.
  //   Unified + campaign members (unified_entries_flat) — those bare columns
  //     were removed; the equivalents now carry a `last_` prefix
  //     (last_domain_id, last_entity_reference_id, …) and mean "this entry's
  //     most recent link only", which is partial whenever the entry has more
  //     than one (entry_domain_count > 1, ~18% of domain-having entries).
  // So the complete arrays are preferred over the `last_` scalars where a
  // single value has to be picked, and the `last_` scalar is only the next
  // fallback. campaign_footprints is gone entirely — the fan-out columns it
  // was folded from no longer exist, and campaign identifiers live on
  // campaign_domains/campaign_entity_references now (a superset of this
  // entry's own links, so deliberately NOT used as a fallback here: it would
  // attribute a co-member's identifier to this entry).
  const flat = item.processed_entries_flat ?? {}
  const firstEntryDomain = flat.entry_domains?.[0]
  const firstEntryEntityReference = flat.entry_entity_references?.[0]
  // Same platform_id -> name lookup as CasesDashboardView's own platformName
  // helper — platform_namelong/platform_text used to be hardcoded null here,
  // which is why every widget reading them (including DomainTakedownWidget's
  // Platform column) always showed a blank/dash regardless of the entry's
  // actual platform.
  const platform = platforms.find((p) => p.id === raw.platform_id)
  // Submitter and Cloudflare request data: the list rows (unified/raw/
  // processed _flat) carry none of it, the /entry-detail refresh merged into
  // `item` does (top-level user_entries.user_id, users(email), and the
  // nested raw_entries row with its *_at_create_time columns).
  const users = Array.isArray(item.users) ? item.users[0] : item.users
  const rawRow = item.raw_entries ?? {}
  return {
    id: item.id,
    user_entry_id: item.id,
    user_id: item.user_id ?? null,
    user_email: users?.email ?? null,
    browser_id: rawRow.browser_id ?? item.browser_id ?? null,
    country_at_create_time: rawRow.country_at_create_time ?? item.country_at_create_time ?? null,
    city_at_create_time: rawRow.city_at_create_time ?? item.city_at_create_time ?? null,
    asn_at_create_time: rawRow.asn_at_create_time ?? item.asn_at_create_time ?? null,
    created_at: raw.created_at ?? item.created_at ?? new Date().toISOString(),
    email_sent: false,
    entry_type: null,
    entity_reference_id: flat.entity_reference_id ?? firstEntryEntityReference?.entity_reference_id ?? flat.last_entity_reference_id ?? null,
    entity_reference_entity: flat.entity_reference_entity ?? firstEntryEntityReference?.entity ?? flat.last_entity_reference_entity ?? null,
    entity_reference_reference: flat.entity_reference_reference ?? firstEntryEntityReference?.reference ?? flat.last_entity_reference_reference ?? null,
    domain_id: flat.domain_id ?? firstEntryDomain?.domain_id ?? flat.last_domain_id ?? null,
    beneficiario: flat.entity_reference_beneficiary ?? firstEntryEntityReference?.beneficiary ?? flat.last_entity_reference_beneficiary ?? null,
    sender: raw.sender ?? null,
    message_content: raw.message_content ?? '',
    scam_claims: raw.scam_claims ?? null,
    platform_namelong: platform?.name_long ?? platform?.name ?? null,
    platform_text: platform?.name ?? null,
    post_type: raw.post_type ?? null,
    // raw.source_type is the real per-row lifecycle-stage indicator
    // unified_entries_flat's own source_type column computes (raw_entry/
    // processed_entry/brand_campaign) — raw_entries_flat/processed_entries_flat
    // use a static per-branch label instead ('raw_entry'/'processed_entry'/
    // 'sCalls'), same three-ish-value shape. raw.source is the free-text
    // ingestion channel (e.g. "twitter"); these were previously swapped here,
    // leaving Source always blank and Source Type showing the channel instead
    // of the stage.
    source_type: raw.source_type ?? null,
    source: raw.source ?? null,
    message_type_id: (raw.post_type === 'call' ? raw.call_type_id : raw.message_type_id) ?? null,
    message_type_text: null,
    raw_free_input: null,
    // raw.r2_key/original_payload/parsed_payload are the real raw_entries
    // column names — present as-is on the old nested-join shape
    // (item.raw_entries), but raw_entries_flat (the flat-row shape most
    // Cases entries come from now) exposes these same values under
    // raw_entry_-prefixed aliases instead, to avoid colliding with its own
    // top-level id/created_at/etc. Falling back to those aliases is what
    // makes the Original Submission and Payload widgets below actually show
    // content for a flat-row entry instead of silently rendering empty.
    r2_key: raw.r2_key ?? raw.raw_entry_r2_key ?? null,
    raw_entry_original_payload: raw.original_payload ?? raw.raw_entry_original_payload ?? null,
    raw_entry_parsed_payload: raw.parsed_payload ?? raw.raw_entry_parsed_payload ?? null,
    // Request location: raw_entry_-prefixed on both flat views (see the
    // payload aliases above), bare column names on the nested-join shape.
    raw_entry_city_at_create_time: raw.raw_entry_city_at_create_time ?? flat.raw_entry_city_at_create_time ?? raw.city_at_create_time ?? null,
    raw_entry_region_at_create_time: raw.raw_entry_region_at_create_time ?? flat.raw_entry_region_at_create_time ?? raw.region_at_create_time ?? null,
    raw_entry_country_at_create_time: raw.raw_entry_country_at_create_time ?? flat.raw_entry_country_at_create_time ?? raw.country_at_create_time ?? null,
    raw_entry_continent_at_create_time: raw.raw_entry_continent_at_create_time ?? flat.raw_entry_continent_at_create_time ?? raw.continent_at_create_time ?? null,
    pipeline_steps: (item.user_entry_pipeline_steps ?? []).map((s) => ({
      step: s.step,
      json_info: s.json_info ?? null,
      created_at: s.created_at,
    })),
  }
}

// Best-effort ProcessedEntry adapter for the domain-intelligence row's
// widgets (DomainIntelligencePanel/BrowserDataPanel/ProbeRunsPanel/
// ProcessedEntryTimeline/LatestObtainedImagesPanel). Fields that don't have
// a real source here (e.g. originalUrl) are left unset — those widgets
// already fall back to the fetched domain's own originalUrl/metadata, same
// tolerance CttEntryDetailPage relies on.
function toProcessedEntryAdapter(item: EntryRow, messageTypeText: string | undefined): ProcessedEntry {
  const raw = item.raw_entries ?? item
  const flat = item.processed_entries_flat ?? {}
  return {
    id: item.id,
    createdAt: raw.created_at ?? item.created_at ?? new Date().toISOString(),
    headline: flat.headline ?? null,
    title: flat.title ?? null,
    messageContent: raw.message_content ?? null,
    sender: raw.sender ?? null,
    processedInputDescription: null,
    postType: raw.post_type ?? null,
    platformNameCode: null,
    platformName: null,
    isScam: getEntryIsScam(item) ?? false,
    userEntryId: item.id,
    // Cases rows still expose user_entry_domain_id; on unified_entries_flat it
    // is last_user_entry_domain_id (this entry's most recent domain link).
    // Either way it's the FK entry_domain_data/entry_browser_data/
    // domain_probe_run hang off, i.e. the way into an entry's probe history.
    userEntryDomainId: flat.user_entry_domain_id ?? flat.last_user_entry_domain_id ?? null,
    articleUrl: flat.article_url ?? null,
    rawEntryR2Key: raw.r2_key ?? flat.raw_entry_r2_key ?? null,
    messageTypeId: (raw.post_type === 'call' ? raw.call_type_id : raw.message_type_id) ?? flat.message_type_id ?? null,
    messageTypeName: flat.message_type_name ?? messageTypeText ?? null,
    sourceType: raw.source ?? flat.source_type ?? null,
  }
}

// Two side-by-side identifier lists in the Campaign section — "carried by
// this entry" vs "spanned by the campaign". highlightAgainst marks the
// entries in this list that are NOT in that other set, i.e. the identifiers
// this entry never touched itself and only reaches through a co-member.
function IdentifierList({
  label,
  domains,
  entityRefs,
  theme,
  highlightAgainst,
}: {
  label: string
  domains: string[]
  entityRefs: string[]
  theme: ProcessedTheme
  highlightAgainst?: string[]
}) {
  const c = watchClasses(theme)
  const items = [...domains, ...entityRefs]
  return (
    <div className={`rounded border p-3 ${c.line}`}>
      <div className={`mb-2 font-mono text-[9px] uppercase tracking-widest ${c.faint}`}>{label}</div>
      {items.length === 0 ? (
        <span className={`text-[12px] ${c.faint}`}>—</span>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {items.map((value) => {
            const viaCoMember = highlightAgainst != null && !highlightAgainst.includes(value)
            return (
              <span
                key={value}
                title={viaCoMember ? 'Only reaches this campaign through another entry — not carried by this one' : undefined}
                className={`rounded border px-1.5 py-0.5 font-mono text-[10px] break-all ${
                  viaCoMember ? 'border-warning-border text-warning' : `${c.line} ${c.muted}`
                }`}
              >
                {value}{viaCoMember ? ' ·via co-member' : ''}
              </span>
            )
          })}
        </div>
      )}
    </div>
  )
}

// Editor-only, hard delete of the whole entry — always behind this confirm
// dialog since /deleteuserentry has no soft-undo on the backend either.
function DeleteEntryButton({ onConfirm, deleting }: { onConfirm: () => void; deleting: boolean }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button
          type="button"
          disabled={deleting}
          title="Delete entry"
          aria-label="Delete entry"
          className="flex items-center gap-1.5 rounded-md border border-destructive/40 px-2.5 py-1 font-mono text-[11px] uppercase tracking-widest text-destructive transition-colors hover:bg-danger-surface disabled:opacity-50"
        >
          {deleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
          Delete
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this entry?</AlertDialogTitle>
          <AlertDialogDescription>
            This permanently removes the entry and everything linked to it (pipeline steps, processed data). This cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => { e.preventDefault(); onConfirm() }}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            Delete entry
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

// Re-exported for existing importers (e.g. SibsMailView) that reach it via
// this file — the real definition now lives in ./CollapsibleSection, pulled
// out so RawProcessedEntryPanel can import it without this file importing
// RawProcessedEntryPanel right back (a circular import, since this page now
// renders that panel below).
export { CollapsibleSection }

export function CaseEntryDetailPage({
  entry: initialEntry,
  theme,
  catalog,
  onClose,
  getScreenshotToken,
  isEditor,
  tenantCode,
  onDeleted,
  onUpdated,
  onSelectCampaign,
  onOpenCandidate,
}: {
  entry: EntryRow
  theme: ProcessedTheme
  catalog: Catalog
  onClose: () => void
  getScreenshotToken: () => Promise<string>
  /** Supply to render the Campaign section, which drills into the derived
   * campaign this entry belongs to. Omitted when the page was opened from
   * inside that campaign already, and never available for Cases-sourced rows
   * (raw_entries_flat carries no campaign_* columns at all). */
  onSelectCampaign?: (campaignId: string) => void
  /** Supply to let an auto-sourced entry (raw_entries.source 'auto_domains')
   * drill into the Domain Candidates record it was promoted from — the
   * Sourcing section renders its "Open candidate" button only when given. */
  onOpenCandidate?: (candidateId: string) => void
  /** Gates the Delete button — backend also enforces Editor-only on
   * /deleteuserentry, this just keeps non-Editors from seeing it at all. */
  isEditor?: boolean
  /** The brand this page was opened under — CasesDashboardView/
   * UnifiedEntriesDashboardView forward their own `storageKey` ('sibs' |
   * 'ctt' | 'santander' | 'cgd' | 'entries' for the brand-agnostic views),
   * which happens to already be exactly the per-brand id this page needs
   * downstream (e.g. DomainTakedownWidget's per-tenant recipient default) —
   * there's no separate tenant/platform field on the row data itself. */
  tenantCode?: string
  /** Called after a successful delete, before onClose, so the caller can
   * drop this entry from its own list/refetch. */
  onDeleted?: (userEntryId: string) => void
  /** Called after a successful assign/accept/reject, so the caller can patch
   * its own list row (e.g. revision_status) without a full refetch. */
  onUpdated?: (userEntryId: string, patch: Partial<EntryRow>) => void
}) {
  const c = watchClasses(theme)
  // Cases' list fetch (raw_entries_flat) never repeats itself while this
  // page is open — none of the Generic-row widgets (pipeline steps,
  // payload, actionable evidence, original submission) had any way to pick
  // up state that changed after the entry was opened (e.g. a classify_retry
  // finishing). Refresh re-fetches this one entry via the same
  // /userentrydetail endpoint the Workbench polls, and merges the result
  // over the initial row — id/created_at/etc. the list fetch already had
  // stay put; whatever this returns (pipeline steps, raw_entries, processed_
  // entries_flat) overwrites the stale copy.
  const [item, setItem] = useState<EntryRow>(initialEntry)
  // Keyed on the id, not the initialEntry object itself: onUpdated (below)
  // patches the caller's `selected`/list row on every handleRefresh, which
  // gives `entry` a new reference on the *same* entry, not just when the
  // user switches to a different one. Resetting `item` to that reference
  // every time would throw away whatever handleRefresh had just merged into
  // it (e.g. domain-linked data only entry-detail's fetch returns) the
  // moment that patch flowed back down — the domain-data widget flashing in
  // then disappearing right after open was exactly this.
  useEffect(() => {
    setItem(initialEntry)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialEntry.id])

  // Domain-linked data (browser captures, probe runs, WHOIS metadata) —
  // fetched separately from `item`, keyed on user_entry_id rather than any
  // single domain id, because it lives on different tables entirely (see
  // /api/campaigns/ctt/entry-domains, which already accepts userEntryId
  // directly and returns every domain on the entry, same call Workbench
  // makes). Used to be keyed on item.processed_entries_flat?.
  // user_entry_domain_id instead — that column existed on the old
  // raw_entries_flat/processed_entries_flat shape, but unified_entries_flat
  // never had a one-domain-per-entry column at all (an entry can have
  // several, see entry_domains on that view), so keying the fetch off it
  // silently never fired for any Unified Entries row. Keying off
  // user_entry_id instead works uniformly for both shapes and for entries
  // with zero, one, or many domains. Declared ahead of handleRefresh below
  // so a refresh can re-trigger this fetch too — it used to only refetch
  // `item` (pipeline steps, processed_entries_flat), so a new
  // entry_browser_data row landing under an existing domain (e.g. right
  // after running the investigation sidebar's Browser Load action) was
  // invisible to every refresh button until a full page reload.
  const [domainData, setDomainData] = useState<EntryDetailData | null>(null)
  const [domainLoading, setDomainLoading] = useState(false)
  const [domainError, setDomainError] = useState<string | null>(null)
  const fetchDomainData = useCallback(async (userEntryId: string) => {
    setDomainLoading(true)
    try {
      const res = await fetch(`/api/campaigns/ctt/entry-domains?userEntryId=${encodeURIComponent(userEntryId)}`)
      const result = await res.json()
      if (result.success) {
        setDomainData(result.data)
        setDomainError(null)
      } else {
        setDomainError(result.reason ?? 'Failed to load domain details')
      }
    } catch {
      setDomainError('Network error loading domain details')
    } finally {
      setDomainLoading(false)
    }
  }, [])
  useEffect(() => {
    setDomainData(null)
    setDomainError(null)
    fetchDomainData(item.id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.id, fetchDomainData])

  // Callers (UnifiedEntriesDashboardView/CasesDashboardView) pass onUpdated
  // as an inline arrow, not a memoized one — a real dependency on it would
  // give handleRefresh a new identity every time the parent re-renders from
  // calling it, which re-fires the effect below that calls handleRefresh,
  // which calls onUpdated again: an infinite entry-detail/domain-data fetch
  // loop. A ref sidesteps that; still always calls the latest onUpdated.
  const onUpdatedRef = useRef(onUpdated)
  useEffect(() => { onUpdatedRef.current = onUpdated }, [onUpdated])

  const [refreshing, setRefreshing] = useState(false)
  // Drives RefreshControl's "updated Xm ago" label — same meaning as any
  // BaseDashboard's lastUpdated: timestamp of the last successful load,
  // starting from when this page itself opened (the initial entry prop is
  // itself a load, not just a refresh).
  const [lastUpdated, setLastUpdated] = useState<number | null>(() => Date.now())
  // List rows arrive with user_entry_pipeline_steps: [] (unified_entries_flat
  // has no steps), so until the first entry-detail fetch lands an empty array
  // means "not loaded yet", not "no verdict". The user-facing preview tile
  // needs that distinction; nothing else on the page does.
  const [detailFetched, setDetailFetched] = useState(false)
  useEffect(() => { setDetailFetched(false) }, [initialEntry.id])
  const handleRefresh = useCallback(async () => {
    setRefreshing(true)
    try {
      const url = `/api/entries/entry-detail?user_entry_id=${encodeURIComponent(initialEntry.id)}`
      const res = await fetch(url)
      const result: { success: boolean; data?: { entry: EntryRow | null } } = await res.json()
      if (result.success && result.data?.entry) {
        setDetailFetched(true)
        setItem((prev) => {
          const fresh = result.data!.entry as EntryRow
          return {
            ...prev,
            ...fresh,
            // /entry-detail builds processed_entries_flat from the
            // processed_entries_flat VIEW, which is a strictly narrower shape
            // than the unified_entries_flat row a list passes in: no
            // campaign_id/campaign_domains, no entry_domains, no last_*
            // columns — and for a still-raw entry there is no row there at
            // all, so it arrives as undefined. Spreading it wholesale
            // therefore wiped the campaign/identifier payload off the entry a
            // moment after this page mounted (this effect runs on mount),
            // which is what made the Campaign section appear and then vanish.
            // Layer the fresher processed fields over the list row instead of
            // replacing it.
            processed_entries_flat: {
              ...((prev.processed_entries_flat ?? {}) as Record<string, unknown>),
              ...((fresh.processed_entries_flat ?? {}) as Record<string, unknown>),
            },
          }
        })
        setLastUpdated(Date.now())
        // /entry-detail returns user_entries' own nested-join shape (top-level
        // user_entries columns + a nested raw_entries/processed_entries), not
        // the flat unified_entries_flat/raw_entries_flat shape the caller's
        // list rows use — spreading the whole response into a list row would
        // shadow e.g. `source` with the wrong table's column (raw_entries.source
        // instead of user_entries.source) via getEntrySourceType's `item.raw_entries
        // ?? item` fallback. Only source (a real top-level user_entries column on
        // both shapes) is safe to patch through as-is.
        onUpdatedRef.current?.(initialEntry.id, { source: result.data.entry.source })
      }
      await fetchDomainData(initialEntry.id)
    } catch (err) {
      console.log('[CaseEntryDetailPage refresh] failed:', err)
    } finally {
      setRefreshing(false)
    }
  }, [initialEntry.id, fetchDomainData])

  const [deleting, setDeleting] = useState(false)
  const handleDelete = useCallback(async () => {
    setDeleting(true)
    try {
      const res = await fetch('/api/entries/delete-user-entry', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ userEntryId: item.id }),
      })
      const result: { success: boolean; error?: string } = await res.json()
      if (!result.success) {
        setDeleting(false)
        window.alert(result.error ?? 'Failed to delete entry')
        return
      }
      onDeleted?.(item.id)
      onClose()
    } catch {
      setDeleting(false)
      window.alert('Network error deleting entry')
    }
  }, [item.id, onClose, onDeleted])

  // Accept/reject/assign-fields/edit-input/classification now live in
  // RawProcessedEntryPanel (shared with SibsMailView's SibsEntryDetailPage)
  // instead of duplicated inline here — see its own render below. revisionStatus/
  // posted/isUnposted stay local since the page's own header badges
  // (revisionBadge, the Processed/Raw badge) need them independently of
  // that panel.
  const revisionStatus = item.revision_status as string | undefined
  const posted = revisionStatus === 'accept' || revisionStatus === 'accept-hidden'
  // "Unposted" here means "not yet converted into a processed entry" —
  // matches the getUnpostedRawEntries precedent (posted=false), but also
  // covers reject/defer'd entries since those can still be reconsidered and
  // accepted later, same as Old Cases allows.
  const isUnposted = !posted

  // Auto-refresh once per entry selection — the prefilled row (initialEntry,
  // from Cases' own list fetch) still renders immediately with no loading
  // flash, this just quietly catches it up right after with whatever's
  // changed since that list load (e.g. a classify_retry that finished after
  // Cases last fetched), same data the manual refresh button pulls.
  useEffect(() => { handleRefresh() }, [handleRefresh])

  const raw = item.raw_entries ?? item
  const isScam = getEntryIsScam(item)
  const revisionBadge = REVISION_BADGE[revisionStatus ?? 'unseen'] ?? { label: revisionStatus ?? 'unseen', tone: 'neutral' as const }
  // Validator entries carry two verdicts: the synchronous bank lookup
  // ("multibanco-validate" step — reference found, beneficiary named) and the
  // later full classification, which is what the scam badge reflects. When
  // the classifier says scam on a reference the bank verified to a named
  // beneficiary, surface the disagreement instead of letting the red badge
  // stand alone — that combination is where the pipeline has been wrong.
  const verifiedBeneficiary = useMemo(() => {
    const step = (item.user_entry_pipeline_steps ?? []).find((s) => s.step === 'multibanco-validate')
    const info = step?.json_info as { lookup_status?: string; beneficiario?: string } | undefined
    if (info?.lookup_status !== 'found') return undefined
    const name = typeof info.beneficiario === 'string' ? info.beneficiario.trim() : ''
    return name || undefined
  }, [item.user_entry_pipeline_steps])
  const badges: DetailBadge[] = [
    ...(isScam ? [{ label: 'scam', tone: 'red' as const }] : []),
    ...(isScam && verifiedBeneficiary
      ? [{ label: `verdict em conflito · referência verificada: ${verifiedBeneficiary}`, tone: 'amber' as const }]
      : []),
    { label: posted ? 'Processed' : 'Raw', tone: posted ? 'emerald' : 'neutral' },
    revisionBadge,
  ]

  useEffect(() => {
    console.log('[CaseEntryDetailPage] entry', item)
  }, [item])

  const messageTypeId = raw.post_type === 'call' ? raw.call_type_id : raw.message_type_id
  const messageTypeText = useMemo(() => {
    if (messageTypeId == null) return undefined
    const list = raw.post_type === 'call' ? catalog.callTypes : catalog.messageTypes
    return list.find((t) => String(t.id) === String(messageTypeId))?.name
  }, [catalog, raw.post_type, messageTypeId])

  // domainData now comes back keyed on user_entry_id (see fetchDomainData
  // above) and can hold several domains for one entry — there's no
  // authoritative "the" domain for an entry with more than one, so this
  // just picks the first (same as Workbench's own curDomain) as the
  // representative shown in the single-domain-shaped widgets below
  // (DomainIntelligencePanel/LatestObtainedImagesPanel/DomainTakedownWidget
  // each take one `domain`, not a list). curDomain must be computed before
  // sibsEntry so that useMemo can use it as a freshness fallback for a
  // domain that registered after this row was last loaded from the view.
  const curDomain = domainData?.domains[0] ?? null
  const sibsEntry = useMemo(() => {
    const base = toSibsEntryAdapter(item, catalog.platforms)
    return { ...base, domain_id: base.domain_id ?? curDomain?.domainId ?? null }
  }, [item, curDomain, catalog.platforms])
  // Read straight off the untouched view row — only unified_entries_flat
  // carries campaign_*, so this stays null for Cases-sourced rows and the
  // section below simply doesn't render.
  const campaignInfo = useMemo(() => {
    const flat = (item.processed_entries_flat ?? {}) as Record<string, any>
    if (!flat.campaign_id) return null
    return {
      campaignId: flat.campaign_id as string,
      campaignType: (flat.campaign_type ?? null) as string | null,
      entryCount: (flat.campaign_entry_count ?? 1) as number,
      entryDomains: (flat.entry_domains ?? []) as EntryDomainLink[],
      entryEntityRefs: (flat.entry_entity_references ?? []) as EntryEntityReferenceLink[],
      campaignDomains: (flat.campaign_domains ?? []) as CampaignDomainIdentifier[],
      campaignEntityRefs: (flat.campaign_entity_references ?? []) as CampaignEntityReferenceIdentifier[],
    }
  }, [item])
  const sourcingProvenance = useMemo(() => {
    const rawRow = (item.raw_entries ?? item) as Record<string, any>
    return readAutoDomainProvenance(rawRow.original_payload ?? rawRow.raw_entry_original_payload ?? null)
  }, [item])
  const processedEntry = useMemo(() => toProcessedEntryAdapter(item, messageTypeText), [item, messageTypeText])
  const evidence = useMemo(() => deriveActionableEvidence(item.user_entry_pipeline_steps ?? []), [item])
  // Bumped when the custom email widget sends, so the Actions widget
  // above it shows the new action without waiting for its poll.
  const [actionsRefresh, setActionsRefresh] = useState(0)
  const communication = useMemo(
    () => caseEntryCommunication(raw, fmtDate(raw.created_at), messageTypeText),
    [raw, messageTypeText],
  )

  // ── Row 2: domain intelligence ──────────────────────────────────────────────
  // (domainData/domainLoading/domainError/fetchDomainData are declared
  // earlier, above handleRefresh, so refresh can re-trigger this fetch too;
  // curDomain itself is declared above sibsEntry, since that widget needs
  // it too.) /entry-domains already scopes everything to this user_entry_id
  // — filtering these down to curDomain.id alone (the previous behavior)
  // silently dropped browser/probe rows belonging to any other domain
  // linked to the same entry. Show every row across every domain instead,
  // same fix Workbench's own curBrowserData/curProbeRuns already applies —
  // BrowserDataPanel/ProbeRunsPanel don't care which domain a row came from.
  const curBrowserData = domainData?.browserData ?? []
  const latestBrowser = curBrowserData.reduce<BrowserRow | null>((latest, row) =>
    !latest || new Date(row.createdAt).getTime() > new Date(latest.createdAt).getTime() ? row : latest, null)
  const curProbeRuns = [...(domainData?.probeRuns ?? [])]
    .sort((a, b) => {
      const nodeCompare = (a.probeNode ?? '').localeCompare(b.probeNode ?? '')
      if (nodeCompare !== 0) return nodeCompare
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    })

  // Investigation sidebar (see InvestigationSidebar's own comment on why
  // it's built generic) — defaults to whatever domain this entry already
  // has on record, falling back to the raw sender, but the operator can
  // freely retarget it (e.g. a URL mentioned in the message body instead of
  // the sender). Only Browser Load has a real backend behind it right now
  // (see /api/entries/probe-domain[-direct] → backend /probedomainproxy or
  // /probedomain → the matching python route); the rest render as visible
  // "Soon" rows until they have one too.
  // curDomain only exists once the separate /entry-domains fetch above
  // resolves (async, keyed off user_entry_id) — it's still null on the render
  // where an entry is first selected. A [item.id]-only effect locked the
  // fallback (raw.sender) in right then and never revisited it once
  // curDomain actually arrived a moment later, permanently showing the
  // sender instead of the identified domain. targetIsAutoRef tracks
  // whether the field's current value is still the auto-derived one (reset
  // per entry, cleared the moment the operator types their own value) so
  // this can re-derive as curDomain shows up without ever clobbering a
  // manual edit.
  const [investigationTarget, setInvestigationTargetState] = useState('')
  const targetIsAutoRef = useRef(true)
  const setInvestigationTarget = (value: string) => {
    targetIsAutoRef.current = false
    setInvestigationTargetState(value)
  }
  useEffect(() => { targetIsAutoRef.current = true }, [item.id])
  useEffect(() => {
    if (!targetIsAutoRef.current) return
    setInvestigationTargetState(curDomain?.originalUrl ?? raw.sender ?? '')
  }, [item.id, curDomain?.originalUrl, raw.sender])

  const investigationActions: InvestigationAction[] = useMemo(() => [
    // Spins until the run's finished/failed pipeline step, then refreshes
    // the page so its browser/domain rows show up (see browserLoadActions).
    // EntryTimelineWidget fetches its own steps and only refetches on
    // actionsRefresh, so bump that too or the run's end never appears there.
    ...browserLoadActions(item.id, () => { handleRefresh(); setActionsRefresh((n) => n + 1) }),
    { id: 'screenshot', label: 'Screenshot', description: 'Full-page evidence capture', icon: Camera },
    { id: 'impersonation', label: 'Impersonation Analysis', description: 'Brand/logo similarity + spoofed content', icon: Fingerprint },
  ], [item.id, handleRefresh])

  return (
    <EntryDetailShell
      mode="page"
      open
      onClose={onClose}
      theme={theme}
      cols={4}
      // An auto-sourced entry's headline is a synthetic sentence about the
      // hostname; the hostname itself is the title a reader wants.
      title={sourcingProvenance?.hostname ?? caseEntryTitle(item)}
      subtitle={fmtDate(raw.created_at)}
      badges={badges}
      headerActions={
        <>
          {isEditor && <DeleteEntryButton onConfirm={handleDelete} deleting={deleting} />}
          <RefreshControl
            theme={theme}
            lastUpdated={lastUpdated}
            onRefresh={handleRefresh}
            refreshing={refreshing}
            storageKey="case-entry-detail"
          />
        </>
      }
      sidebar={
        <InvestigationSidebar
          theme={theme}
          target={investigationTarget}
          onTargetChange={setInvestigationTarget}
          actions={investigationActions}
        />
      }
    >
      <GenericEntryRow
        theme={theme}
        entry={sibsEntry}
        catalog={catalog}
        steps={item.user_entry_pipeline_steps ?? []}
        stepsReady={detailFetched}
        onRefresh={handleRefresh}
        refreshing={refreshing}
        submission={{
          r2Key: raw.r2_key ?? raw.raw_entry_r2_key,
          getToken: getScreenshotToken,
          communication,
          postType: raw.post_type,
          messageTypeId,
          messageTypeText,
        }}
        preview={{
          raw,
          processed: Array.isArray(item.processed_entries) ? item.processed_entries[0] : item.processed_entries,
          source: (item.source as string | null | undefined) ?? raw.source,
          userEntryId: item.id,
        }}
      />

      {/* Stays visible after the entry is posted too — re-editing an
          already-approved entry (fixing a platform, correcting content,
          reclassifying) is exactly the case this panel needs to support,
          not just the pre-approval flow. Collapsed by default once posted
          so an already-handled entry doesn't default to looking "still
          needs action". onUpdated reuses this page's own handleRefresh
          (item + domain data), same as its mount effect and Accept both
          already did before this panel was pulled out into its own
          component — RawProcessedEntryPanel does its own independent
          live fetch internally now, this just keeps the rest of the page
          (domain intelligence etc.) in sync with whatever it just changed. */}
      <RawProcessedEntryPanel userEntryId={item.id} theme={theme} defaultOpen={isUnposted} onUpdated={handleRefresh} />

      {/* Auto-sourced entries: the sourcing run is the reporter, so its
          provenance (strategy/gram, verdict, analysis) is this entry's
          "original submission". Present only when raw_entries.original_payload
          carries the block promote_candidate() writes. */}
      {sourcingProvenance && (
        <SourcingProvenanceSection
          provenance={sourcingProvenance}
          entryDomainUrl={campaignInfo?.entryDomains?.[0]?.domain_url ?? null}
          theme={theme}
          onOpenCandidate={onOpenCandidate}
        />
      )}

      {/* Only for unified_entries_flat-backed rows: Cases rows come from
          raw_entries_flat, which has no campaign_* columns, so there is
          nothing to derive a campaign from there. */}
      {onSelectCampaign && campaignInfo && (
        <CollapsibleSection label="Campaign" theme={theme} defaultOpen>
          <div className="flex flex-wrap items-center gap-2">
            <span className={`inline-flex items-center gap-1 rounded border px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest ${
              campaignInfo.entryCount > 1 ? 'border-signal/30 text-signal' : `${watchClasses(theme).line} ${watchClasses(theme).faint}`
            }`}>
              {campaignInfo.campaignType ?? 'campaign'} · {campaignInfo.entryCount} {campaignInfo.entryCount === 1 ? 'entry' : 'entries'}
            </span>
            <button
              type="button"
              onClick={() => onSelectCampaign(campaignInfo.campaignId)}
              className={`rounded border px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest transition-colors hover:text-signal hover:border-signal ${watchClasses(theme).line} ${watchClasses(theme).faint}`}
            >
              {campaignInfo.entryCount > 1
                ? `Open campaign · see the other ${campaignInfo.entryCount - 1}`
                : 'Open campaign'}
            </button>
          </div>
          <p className={`mt-3 text-[12px] ${watchClasses(theme).muted}`}>
            {campaignInfo.entryCount > 1
              ? 'This entry shares an identifier with the other members below — that shared identifier is the whole reason they are one campaign.'
              : 'No other entry shares an identifier with this one yet, so the campaign is just this entry.'}
          </p>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <IdentifierList
              label="Carried by this entry"
              domains={campaignInfo.entryDomains.map((d) => d.domain_url)}
              entityRefs={campaignInfo.entryEntityRefs.map((r) => `${r.entity} / ${r.reference}`)}
              theme={theme}
            />
            <IdentifierList
              label="Spanned by the campaign"
              // Deliberately the campaign's full set, not this entry's: when
              // it lists something the left column doesn't, that identifier
              // reached the campaign through a co-member, which is precisely
              // what explains the grouping.
              domains={campaignInfo.campaignDomains.map((d) => d.domain_url)}
              entityRefs={campaignInfo.campaignEntityRefs.map((r) => `${r.entity} / ${r.reference}`)}
              theme={theme}
              highlightAgainst={[
                ...campaignInfo.entryDomains.map((d) => d.domain_url),
                ...campaignInfo.entryEntityRefs.map((r) => `${r.entity} / ${r.reference}`),
              ]}
            />
          </div>
        </CollapsibleSection>
      )}

      {/* Unlike the old domainId-gated version, this always renders — an
          entry's domain data is now only known once the /entry-domains
          fetch above resolves (it's no longer readable synchronously off
          any single column), so gating the section itself on that same
          data produced a flash of "no section at all" indistinguishable
          from "still loading". curDomain ? ... : ... below carries that
          distinction instead, same structure as Workbench's own Domain
          intelligence section. */}
      {/* The entry's whole story on one axis -- sourced, received,
          classified, domain and WHOIS, captures and probes, every action,
          email and reply -- from the entry_timeline_event view. Keyed on
          the user_entry_id, like Actions taken, and refreshed with it. */}
      {sibsEntry.user_entry_id && (
        <CollapsibleSection label="Timeline" theme={theme} defaultOpen>
          <EntryTimelineWidget userEntryId={sibsEntry.user_entry_id} theme={theme} refreshKey={actionsRefresh} className="" />
        </CollapsibleSection>
      )}

      <CollapsibleSection label="Domain intelligence" theme={theme} defaultOpen>
        <div className={`grid gap-3 ${detailGridCols(3)}`}>
          {domainError ? (
            <div className="col-span-3 font-mono text-xs text-destructive">{domainError}</div>
          ) : curDomain ? (
            <>
              <ProcessedEntryTimeline entry={processedEntry} domain={curDomain} browserData={curBrowserData} theme={theme} />
              <LatestObtainedImagesPanel
                entryId={processedEntry.id}
                userEntryDomainId={curDomain.id}
                latestBrowser={latestBrowser}
                theme={theme}
                getToken={getScreenshotToken}
              />
              <DomainIntelligencePanel entry={processedEntry} domain={curDomain} theme={theme} />
              <BrowserDataPanel curBrowserData={curBrowserData} latestBrowser={latestBrowser} theme={theme} />
              <ProbeRunsPanel curProbeRuns={curProbeRuns} theme={theme} />
            </>
          ) : (
            <div className="col-span-3 font-mono text-[11px] uppercase tracking-widest opacity-60">
              {domainLoading ? 'Checking for a registered domain…' : 'No domain registered on this entry yet'}
            </div>
          )}
        </div>
      </CollapsibleSection>

      {/* Actions taken: the takedown reports for the entry's domain -- what
          the planner decided per channel, what was sent, what came back --
          the operator's own email as one more tracked channel, and below
          them the manual sends (SIBS alerts, case reports) this entry
          already carries. Anchored on the user_entry_id, which the promoted
          candidate shares, so the same widget sits on the candidate's
          detail. Without a domain there is nothing to plan, and only the
          manual section shows -- hence hasDomain rather than a gate. */}
      {sibsEntry.user_entry_id && (
        <CollapsibleSection label="Actions taken" theme={theme} defaultOpen>
          <div className="flex flex-col gap-4">
            <ActionsWidget
              userEntryId={sibsEntry.user_entry_id}
              hostname={curDomain?.originalUrl ?? null}
              hasDomain={!!curDomain || evidence.domains.length > 0}
              theme={theme}
              refreshKey={actionsRefresh}
            />
            {curDomain && (
              <div className={`border-t pt-3 border-border`}>
                <DomainTakedownWidget
                  entry={sibsEntry}
                  userEntryId={sibsEntry.user_entry_id}
                  evidence={evidence}
                  domainUrl={curDomain?.originalUrl ?? null}
                  browserRows={curBrowserData}
                  browserRowsLoading={domainLoading}
                  tenantCode={tenantCode}
                  theme={theme}
                  getToken={getScreenshotToken}
                  onSent={() => setActionsRefresh((n) => n + 1)}
                />
              </div>
            )}
          </div>
        </CollapsibleSection>
      )}
    </EntryDetailShell>
  )
}
